/**
 * client/render/missiles.ts — projectiles, stuck projectiles and pickups. OWNER: RENDER agent.
 *
 * Models [WPN §2-4, models-anims.md §2]:
 *   BOLT            flying + stuck: w_crossbow_bolt_exp.glb (t5_weapon_crossbow_bolt_exp). The stuck grenade
 *                   model t5_weapon_crossbow_bolt was not exported -> the flight model is reused (ESTIMATE).
 *                   Stuck & armed: red blink (green for your own bolt) at tag_fx at the BOLT_BEEP_* schedule.
 *   BALLISTIC_KNIFE flying: w_ballistic_knife_projectile; stuck/pickup: the same + the glowing
 *                   w_ballistic_knife_blade_retrieve overlay (_ballistic_knife.gsc).
 *   HATCHET         flying: w_hatchet_world, spinning end over end ("Rotate one axis, blade style");
 *                   pickup/stuck: w_hatchet_world + w_hatchet_world_retrieve glow (worldModel3).
 * Flying bolts/knives point along their velocity (projExplosionType bolt: rotation follows velocity).
 * Trails: fx_trail_crossbow / fx_trail_knife_ballistic / fx_trail_hatchet (fx.ts).
 */
import * as THREE from 'three';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { vectorToAngles, type Vec3 } from '@shared/math';
import { MissileKind, MissileState } from '@shared/types';
import type { ClientWorld, RenderMissile } from '../types';
import { placeCod } from './codxform';
import type { FxSystem } from './fx';
import { boltBeepIndex, boltBlinkOn, hatchetSpin } from './fxmath';
import type { Bo2Uniforms } from './glsl';
import { makeBo2ModelMaterial } from './modelmat';
import { disposeSkeletons, presetSkinnedBounds, type ModelCache } from './modelcache';
import type { ProbeGrid } from './players';

const FILES = {
  bolt: 'models/w_crossbow_bolt_exp.glb',
  knife: 'models/w_ballistic_knife_projectile.glb',
  knifeGlow: 'models/w_ballistic_knife_blade_retrieve.glb',
  hatchet: 'models/w_hatchet_world.glb',
  hatchetGlow: 'models/w_hatchet_world_retrieve.glb',
} as const;
type ModelKey = keyof typeof FILES;

/** Retrieve-model glow (ESTIMATE: unlit additive, pulsing ~1 Hz). */
const GLOW_INTENSITY = 1.6;

interface MissileView {
  root: THREE.Group;
  model: THREE.Object3D;
  glow: THREE.Object3D | null;
  mats: THREE.ShaderMaterial[];
  glowMats: THREE.ShaderMaterial[];
  kind: MissileKind;
  fx: THREE.Object3D | null;
  firstSeen: number;
  lastTrail: number;
  key: ModelKey;
  lastBeep: number;
}

export class MissileRenderer {
  private readonly models = new Map<ModelKey, GLTF>();
  private readonly views = new Map<number, MissileView>();
  private readonly tmp: Vec3 = [0, 0, 0];
  private readonly ang: Vec3 = [0, 0, 0];
  private readonly v3 = new THREE.Vector3();
  private readonly amb = new THREE.Vector3();
  private time = 0;

  constructor(private readonly cache: ModelCache, private readonly U: Bo2Uniforms, private readonly scene: THREE.Scene, private readonly fx: FxSystem) {}

  async load(): Promise<void> {
    await Promise.all((Object.keys(FILES) as ModelKey[]).map(async (k) => { this.models.set(k, await this.cache.load('/assets/' + FILES[k])); }));
  }

  get count(): number { return this.views.size; }

  private instance(key: ModelKey, glow: boolean): { obj: THREE.Object3D; mats: THREE.ShaderMaterial[] } {
    const g = this.models.get(key)!;
    const obj = skeletonClone(g.scene);
    presetSkinnedBounds(obj); // else three skins every vertex on the CPU at the first draw
    const mats: THREE.ShaderMaterial[] = [];
    obj.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.frustumCulled = false;
      const list = Array.isArray(m.material) ? m.material : [m.material];
      const nm = list.map((x) => {
        const bm = makeBo2ModelMaterial(x, this.U, glow ? { additive: true, emissive: GLOW_INTENSITY, receiveSun: false } : {});
        mats.push(bm);
        return bm;
      });
      m.material = Array.isArray(m.material) ? nm : nm[0];
      if (glow) m.renderOrder = 50;
    });
    return { obj, mats };
  }

  private create(ms: RenderMissile): MissileView {
    const key: ModelKey = ms.kind === MissileKind.BOLT ? 'bolt' : ms.kind === MissileKind.BALLISTIC_KNIFE ? 'knife' : 'hatchet';
    const root = new THREE.Group();
    root.name = `missile_${ms.entId}`;
    const { obj, mats } = this.instance(key, false);
    root.add(obj);
    let glow: THREE.Object3D | null = null;
    let glowMats: THREE.ShaderMaterial[] = [];
    if (ms.kind !== MissileKind.BOLT) {
      const gi = this.instance(ms.kind === MissileKind.BALLISTIC_KNIFE ? 'knifeGlow' : 'hatchetGlow', true);
      glow = gi.obj; glowMats = gi.mats;
      glow.visible = false;
      root.add(glow);
    }
    this.scene.add(root);
    return { root, model: obj, glow, mats, glowMats, kind: ms.kind, fx: obj.getObjectByName('tag_fx') ?? null, firstSeen: this.time, lastTrail: 0, key, lastBeep: -1 };
  }

  update(world: ClientWorld, dtMs: number, probes: ProbeGrid | null): void {
    this.time += dtMs;
    const seen = new Set<number>();
    for (const ms of world.missiles) {
      seen.add(ms.entId);
      let v = this.views.get(ms.entId);
      if (!v || v.kind !== ms.kind) { if (v) this.remove(ms.entId, v); v = this.create(ms); this.views.set(ms.entId, v); }
      this.place(v, ms, world);
      if (probes) {
        probes.sample(this.amb, v.root.position);
        for (const m of v.mats) (m.uniforms.uAmbient.value as THREE.Vector3).copy(this.amb);
      }
    }
    for (const [id, v] of this.views) if (!seen.has(id)) this.remove(id, v);
  }

  private place(v: MissileView, ms: RenderMissile, world: ClientWorld): void {
    const flying = ms.state === MissileState.FLYING;
    const speed2 = ms.velocity[0] ** 2 + ms.velocity[1] ** 2 + ms.velocity[2] ** 2;
    if (flying && speed2 > 1) {
      vectorToAngles(this.ang, ms.velocity);
      if (v.kind === MissileKind.HATCHET) {
        // end-over-end spin about the hatchet's lateral axis
        this.ang[0] = (this.ang[0] + hatchetSpin(this.time - v.firstSeen)) % 360;
      }
    } else if (v.kind === MissileKind.HATCHET && ms.state === MissileState.PICKUP) {
      // a hatchet at rest lies on its side (the model stands upright, handle along -Z, at angles 0); the sim's
      // rest angles are not a reliable model orientation (ESTIMATE: keep its yaw, roll 90)
      this.ang[0] = 0; this.ang[1] = ms.angles[1]; this.ang[2] = 90;
    } else {
      this.ang[0] = ms.angles[0]; this.ang[1] = ms.angles[1]; this.ang[2] = ms.angles[2];
    }
    placeCod(v.root, ms.origin, this.ang);
    if (v.kind === MissileKind.HATCHET && ms.state === MissileState.PICKUP) v.root.position.y += 1.2; // half thickness
    v.root.updateMatrixWorld(true);
    // trails while flying
    if (flying && this.time - v.lastTrail > 16) {
      v.lastTrail = this.time;
      (v.fx ?? v.root).getWorldPosition(this.v3);
      this.fx.trail(this.v3, v.kind === MissileKind.BOLT ? 'bolt' : v.kind === MissileKind.BALLISTIC_KNIFE ? 'knife' : 'hatchet');
    }
    // retrieve glow on pickups (knife / axe at rest)
    if (v.glow) {
      const atRest = ms.state === MissileState.PICKUP || ms.state === MissileState.STUCK_WORLD;
      v.glow.visible = atRest;
      if (atRest) {
        const pulse = 0.65 + 0.35 * Math.sin((this.time / 1000) * Math.PI * 2);
        for (const m of v.glowMats) m.uniforms.uEmissive.value = GLOW_INTENSITY * pulse;
      }
    }
    // explosive bolt blink
    if (v.kind === MissileKind.BOLT && ms.state !== MissileState.FLYING && ms.explodeAt > 0) {
      const k = boltBeepIndex(world.renderTime, ms.explodeAt);
      if (k >= 0 && k !== v.lastBeep && boltBlinkOn(world.renderTime, ms.explodeAt)) {
        v.lastBeep = k;
        (v.fx ?? v.root).getWorldPosition(this.v3);
        this.fx.blink(this.v3, ms.owner === world.localClientNum);
      }
    }
  }

  private remove(id: number, v: MissileView): void {
    this.scene.remove(v.root);
    for (const m of [...v.mats, ...v.glowMats]) m.dispose();
    disposeSkeletons(v.root); // per-clone bone textures (else one GL texture leaks per projectile)
    this.views.delete(id);
  }

  /** Load-time warm-up (see PlayerRenderer.prewarm): one hidden instance of every projectile model. */
  prewarm(at: THREE.Vector3): (show: boolean) => void {
    const objs: THREE.Object3D[] = [];
    let i = 0;
    for (const key of Object.keys(FILES) as ModelKey[]) {
      const { obj, mats } = this.instance(key, key === 'knifeGlow' || key === 'hatchetGlow');
      obj.position.copy(at).add(new THREE.Vector3(i++ * 12, 30, 0));
      obj.name = `warm_missile_${key}`;
      obj.updateMatrixWorld(true);
      this.scene.add(obj);
      objs.push(obj);
      this.warmMats.push(...mats);
    }
    this.warm = objs;
    return (show) => { for (const o of objs) o.visible = show; };
  }
  private warm: THREE.Object3D[] = [];
  private readonly warmMats: THREE.ShaderMaterial[] = [];

  dispose(): void {
    for (const [id, v] of this.views) this.remove(id, v);
    for (const o of this.warm) { this.scene.remove(o); disposeSkeletons(o); }
    for (const m of this.warmMats) m.dispose();
    this.warm = [];
  }
}
